// RAG Dust (decision de Louka du 01/10) : Dust devient la seule source des documents. L'onglet
// Entreprise de la barre montre l'arbre des espaces de Dust tel quel (SpaceSideBarMenu : espaces
// ouverts, espaces restreints, dossiers, sites, outils), avec la creation d'espace pour un admin,
// comme dans SpaceLayout.
// Espaces restreints (08/10) : quand le plan n'autorise plus d'espace, le bouton « Nouvel espace » reste
// et explique pourquoi (au lieu de disparaitre sans un mot, ce qui laissait l'admin sans piste).
// Page Connecteurs (22/10) : un membre n'a pas l'espace systeme (ou vit « Connecteurs » des admins) ;
// il a sa propre entree, vers la page ou il connecte ses comptes.
import { CreateOrEditSpaceModal } from "@app/components/spaces/CreateOrEditSpaceModal";
import SpaceSideBarMenu from "@app/components/spaces/SpaceSideBarMenu";
import { useAuth } from "@app/lib/auth/AuthContext";
import { t } from "@app/lib/compta/i18n";
import { routeConnecteurs } from "@app/lib/compta/page_connecteurs";
import { useAppRouter } from "@app/lib/platform";
import { isPrivateSpacesLimitReached } from "@app/lib/spaces";
import { useSpacesAsAdmin } from "@app/lib/swr/spaces";
import type { LightWorkspaceType } from "@app/types/user";
import {
  CloudArrowLeftRight,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NavigationList,
  NavigationListItem,
} from "@dust-tt/sparkle";
import { useCallback, useState } from "react";

export function EspacesDeDust({ owner }: { owner: LightWorkspaceType }) {
  const { subscription, isAdmin } = useAuth();
  const plan = subscription.plan;
  const router = useAppRouter();
  const [creation, setCreation] = useState({
    isOpen: false,
    defaultRestricted: false,
  });
  const { spaces } = useSpacesAsAdmin({
    workspaceId: owner.sId,
    disabled: plan.limits.vaults.maxVaults === 0 || !isAdmin,
  });
  const limiteAtteinte = isPrivateSpacesLimitReached(spaces, plan);
  const ouvrirCreation = useCallback(
    ({ defaultRestricted }: { defaultRestricted: boolean }) =>
      setCreation({ isOpen: true, defaultRestricted }),
    []
  );
  const fermer = useCallback(
    () => setCreation((c) => ({ ...c, isOpen: false })),
    []
  );

  const pageConnecteurs = routeConnecteurs(owner.sId);

  return (
    <>
      {!isAdmin && (
        <NavigationList>
          <NavigationListItem
            label="Connecteurs"
            icon={CloudArrowLeftRight}
            selected={router.asPath === pageConnecteurs}
            onClick={() => void router.push(pageConnecteurs)}
          />
        </NavigationList>
      )}
      <SpaceSideBarMenu
        owner={owner}
        isAdmin={isAdmin}
        openSpaceCreationModal={isAdmin ? ouvrirCreation : undefined}
      />
      {isAdmin && !limiteAtteinte && (
        <CreateOrEditSpaceModal
          isAdmin={isAdmin}
          owner={owner}
          isOpen={creation.isOpen}
          onClose={fermer}
          onCreated={(space) => {
            void router.push(`/w/${owner.sId}/spaces/${space.sId}`);
          }}
          defaultRestricted={creation.defaultRestricted}
        />
      )}
      {isAdmin && limiteAtteinte && (
        <Dialog
          open={creation.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              fermer();
            }
          }}
        >
          <DialogContent size="md" isAlertDialog>
            <DialogHeader hideButton>
              <DialogTitle>{t("You can't create more spaces.")}</DialogTitle>
            </DialogHeader>
            <DialogContainer>
              {t(
                "The maximum number of spaces for your subscription has been reached. To create another space, ask for the limit to be raised."
              )}
            </DialogContainer>
            <DialogFooter
              rightButtonProps={{
                label: t("Ok"),
                variant: "outline",
                onClick: fermer,
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
